export const SEW_METADATA_KEYS = {
  billingRegion: 'sew_billing_region',
  customer: 'sew_customer_name',
  sewCustomerId: 'sew_customer_id',
  shippingCustomer: 'sew_shipping_customer_name',
  shippingCustomerId: 'sew_shipping_customer_id',
  billingCustomer: 'sew_billing_customer_name',
  billingCustomerId: 'sew_billing_customer_id',
  orderNumber: 'sew_order_number',
  billingType: 'sew_billing_type',
} as const;

// SAP customer roles. Sold-to reuses the original customer keys, so a tenant
// whose parties all match keeps exactly the metadata it had before.
export const SEW_CUSTOMER_PARTIES = [
  {
    party: 'sold_to',
    nameKey: SEW_METADATA_KEYS.customer,
    idKey: SEW_METADATA_KEYS.sewCustomerId,
  },
  {
    party: 'ship_to',
    nameKey: SEW_METADATA_KEYS.shippingCustomer,
    idKey: SEW_METADATA_KEYS.shippingCustomerId,
  },
  {
    party: 'bill_to',
    nameKey: SEW_METADATA_KEYS.billingCustomer,
    idKey: SEW_METADATA_KEYS.billingCustomerId,
  },
] as const;

// Only sent when the parties differ from sold-to.
export const SEW_SEPARATE_PARTY_KEYS = [
  SEW_METADATA_KEYS.shippingCustomer,
  SEW_METADATA_KEYS.shippingCustomerId,
  SEW_METADATA_KEYS.billingCustomer,
  SEW_METADATA_KEYS.billingCustomerId,
] as const;

export const SEW_CUSTOMER_PARTIES_MODE = {
  same: 'same',
  separate: 'separate',
} as const;

export const SEW_CUSTOMER_PARTIES_MODE_VALUES = [
  SEW_CUSTOMER_PARTIES_MODE.same,
  SEW_CUSTOMER_PARTIES_MODE.separate,
] as const;

// Form-only field: the choice itself is never persisted, it's re-derived from
// the presence of the ship-to/bill-to keys.
export const SEW_CUSTOMER_PARTIES_MODE_FIELD = 'sewCustomerPartiesMode';

// Drops SEW-specific keys from a tenant metadata blob (preserving any other
// keys) so they never reach a non-SEW reseller. Returns undefined when nothing
// remains, so an empty object isn't sent.
export const omitSewMetaData = <T extends Record<string, string | undefined>>(
  metaData?: T | null,
): T | undefined => {
  if (!metaData) return undefined;

  const sewKeys = new Set<string>(Object.values(SEW_METADATA_KEYS));
  const entries = Object.entries(metaData).filter(([key]) => !sewKeys.has(key));

  return entries.length ? (Object.fromEntries(entries) as T) : undefined;
};

// Strips the ship-to/bill-to keys unless the parties actually differ, and drops
// blanks left behind by toggling the choice back and forth. metaData is stored
// as one blob, so an omitted key is a removed key.
export const applySewCustomerParties = <
  T extends Record<string, string | undefined>,
>(
  metaData?: T | null,
  hasSeparateParties?: boolean,
): T | undefined => {
  if (!metaData) return undefined;

  const separateKeys = new Set<string>(SEW_SEPARATE_PARTY_KEYS);
  const entries = Object.entries(metaData).filter(([key, value]) => {
    if (!separateKeys.has(key)) return true;

    return Boolean(hasSeparateParties && value);
  });

  return Object.fromEntries(entries) as T;
};

export const getSewCustomerPartiesMode = (
  metaData?: Record<string, unknown> | null,
) => {
  const hasSeparateParties = SEW_SEPARATE_PARTY_KEYS.some(
    (key) => metaData?.[key],
  );

  return hasSeparateParties
    ? SEW_CUSTOMER_PARTIES_MODE.separate
    : SEW_CUSTOMER_PARTIES_MODE.same;
};
